import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const solutionsShowcase = [
  {
    image: "/assets/images/1.jpeg",
    arTitle: "رفوف مستودعات ثقيلة",
    arDesc: "لتخزين الأحمال الكبيرة وتنظيم مسارات العمل داخل المستودع.",
    enTitle: "Heavy-Duty Warehouse Racking",
    enDesc: "For heavy-load storage and efficient warehouse flow.",
  },
  {
    image: "/assets/images/2.jpeg",
    arTitle: "رفوف تحميل متوسط",
    arDesc: "حل عملي للمخازن التجارية التي تحتاج مرونة وسهولة وصول.",
    enTitle: "Medium-Duty Shelving",
    enDesc: "A practical layout for commercial storage with flexible access.",
  },
  {
    image: "/assets/images/3.jpeg",
    arTitle: "رفوف عرض المتاجر",
    arDesc: "تصميم يساعد على إبراز المنتجات وتحسين تجربة العميل داخل الفرع.",
    enTitle: "Retail Display Shelving",
    enDesc: "Designed to improve product visibility and customer browsing.",
  },
  {
    image: "/assets/images/4.jpeg",
    arTitle: "حلول رفوف مخصصة",
    arDesc: "تفصيل المقاسات والتكوين حسب مساحة الموقع وطبيعة النشاط.",
    enTitle: "Custom Shelving Solutions",
    enDesc: "Tailored sizing and configuration based on your operational space.",
  },
  {
    image: "/assets/images/5.jpeg",
    arTitle: "رفوف السوبر ماركت",
    arDesc: "أنظمة متكاملة لعرض المنتجات وتنظيم الممرات بكفاءة.",
    enTitle: "Supermarket Shelving",
    enDesc: "Integrated systems for product display and aisle organization.",
  },
  {
    image: "/assets/images/6.jpeg",
    arTitle: "أنظمة تنظيم المخزون",
    arDesc: "تقليل الهدر وتحسين دورة التخزين عبر توزيع واضح للأصناف.",
    enTitle: "Inventory Organization Systems",
    enDesc: "Reduce storage waste with clear, structured product allocation.",
  },
  {
    image: "/assets/images/7.jpeg",
    arTitle: "تهيئة المساحات التشغيلية",
    arDesc: "حلول تساعد على استغلال المساحة ورفع كفاءة التشغيل اليومية.",
    enTitle: "Operational Space Setup",
    enDesc: "Layouts that maximize available area and daily work efficiency.",
  },
];

export default function Testimonials() {
  const { i18n } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const isRTL = i18n.language?.startsWith("ar");

  return (
    <section id="solutions-showcase" className="w-full py-16 md:py-24 bg-[#F0F0F0]">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div ref={ref} className="text-center mb-12 md:mb-16">
          <motion.div
            className="inline-flex items-center gap-2 mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <i className="ri-grid-line text-brand" />
            <span className="text-sm font-medium text-brand tracking-wide uppercase">
              {isRTL ? "أنظمة التخزين" : "Storage Systems"}
            </span>
          </motion.div>
          <motion.h2
            className="text-3xl md:text-5xl font-bold text-stone-900 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {isRTL ? "اختر النمط المناسب لطبيعة مخزونك" : "Choose the Right Layout for Your Inventory"}
          </motion.h2>
          <motion.p
            className="mt-4 max-w-3xl mx-auto text-base md:text-lg text-stone-600"
            initial={{ opacity: 0, y: 22 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 }}
            transition={{ duration: 0.5, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {isRTL
              ? "هذا القسم يعرض أنواعًا مختلفة من أنظمة التخزين التي نوفرها لتناسب المستودعات والمتاجر باختلاف المساحات والمتطلبات."
              : "This section showcases different shelving solution types tailored for warehouses and retail spaces."}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {solutionsShowcase.map((item, index) => (
            <motion.figure
              key={item.image}
              className={`group relative overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-[0_8px_24px_rgba(0,0,0,0.05)] ${
                index === 0 ? "sm:col-span-2 lg:col-span-2" : ""
              }`}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.5, delay: 0.1 * index, ease: [0.25, 0.1, 0.25, 1] }}
              whileHover={{ y: -6 }}
            >
              <div className="relative h-56 md:h-64 overflow-hidden">
                <img
                  src={item.image}
                  alt={isRTL ? item.arTitle : item.enTitle}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
                <figcaption className="absolute bottom-0 left-0 right-0 px-4 py-3 text-white">
                  <p className="text-sm md:text-base font-semibold mb-1">
                    {isRTL ? item.arTitle : item.enTitle}
                  </p>
                  <p className="text-xs md:text-sm text-white/85">
                    {isRTL ? item.arDesc : item.enDesc}
                  </p>
                </figcaption>
              </div>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}